/**
 * @file    connection.js
 *          Implementation #2 of the Connection class for dcp5 (See dcp-docs-wes, protocol-api.md)
 * @author  Wes Garland, wes@distributive.network
 * @date    Nov 2023
 *
 * The Connection class is used to implement the DCPv5 protocol. This protocol provides peer-to-peer
 * messaging capabilities with an RPC flavour, with message non-repudiation and replay attack
 * prevention. These features do not require a secure bearer.
 *
 * The basic idea is that initiators initiate connections, and targets accept connections from
 * initators. Beyond this detail, and details with respect to clock skew management for message
 * TTLs, there is no functional difference between an initiator and a target.
 *
 * ## Implementation status
 *
 * A full API and wire-level grammar are specified in protocol-api.md in the dcp-docs-wes repo. This
 * implementation is based on version 2.0 of that document, with a number of small changes which are
 * only available (as of Nov 2023) in red ink on the paper version of that document in my office.
 *
 * The following features from dcp520 are not yet implemented:
 * - auth
 * - OOB close
 * - ntp, time shift adjust
 * - early peer address from config
 *
 * ## Nonces, Acks
 * Every DcpMessage send over the transport bears a cryptographic nonce and ackToken. The nonce is
 * the last nonce received from the peer, and the ackToken is freshly-generated. An entity receiving a
 * DcpMessage responds with an Ack that has this ackToken, and a new nonce. AckTokens and nonces may
 * only be used once. The first nonce is null, and is only valid for either a 'connect' or 'close'
 * operation.
 *
 * ## Session Negotiation
 * The first request is a connect operation bearing 1/2 the dcpsid, and this is answered with a
 * connect response containing the entire dcpsid. This marks the beginning of the session.
 *
 * ## DcpMessage Transmission
 * When messages are sent, they are pushed on the transmitTxQueue FIFO. This FIFO is basically a
 * message queue, and the queue is (mostly) pumped by the arrival of Acks from the peer. More than
 * one message can be pulled from the queue for a single ack, after session establishment, by
 * means of Batch messages.
 *
 * ## State Transition Diagram:
 *
 * initial      connecting           established         disconnected      closing       closed
 * ============================================================================================
 *   |- connect ---->                                                                            # initiator only
 *   |- accept ----->                                                                            # target only
 *                  |- negotiateSession -->                                                      # only if never established
 *                  |- resumeSession -----><------------------|                                  # only if previously established
 *                  |- hangupHandler -----|------------------->
 *                  <------------------------------- connect -|                                  # initiator only
 *   |- shutdown ---|---------------------|-------------------|-------------->
 *                                                                           |- finalize -->
 * connecting:  can send connect messages
 * established: can send and receive all messages
 * closing:     can send all responses and connect/close requests
 */
'use strict';

const semver = require('semver');
const chalk  = require('dcp/utils').chalk();
const wallet = require('dcp/dcp-client/wallet');
const dcpEnv = require('dcp/common/dcp-env');

const { requireNative }           = require('dcp/dcp-client/webpack-native-bridge');
const { EventEmitter }            = require('events');
const { assert }                  = require('dcp/common/dcp-assert');
const { leafMerge,
        a$sleep,
        Backoff }                 = require('dcp/utils');
const { Synchronizer }            = require('dcp/common/concurrency');
const { DCPError }                = require('dcp/common/dcp-error');
const { DcpURL }                  = require('dcp/common/dcp-url');
const { errorPayloadCtorFactory } = require('./error-payload');
const { requestCtorFactory,
        responseCtorFactory,
        notificationCtorFactory,
        batchCtorFactory }        = require('./dcp-message');
const { ackCtorFactory }          = require('./ack');
const { setImmediate,
        setTimeout,
        clearTimeout,
        Reference }               = require('dcp/common/dcp-timers');
const { randStr21,
        wireLog }                 = require('./utils');
const { DialogueRegistry }        = require('./dialogue-registry');
const { getGlobalIdentityCache } = require('./identity-cache');

const debugging = require('dcp/debugging').scope('dcp');
const usingDebugger = dcpEnv.platform === 'nodejs' && requireNative('module')._cache.niim instanceof requireNative('module').Module;

const versionInfo = {
  implementation: '5.3.0', /* our version */
  compatibility: '^5.2.0', /* who we're willing to talk to */
};

/* Process-wide state */
const globalBackoffs = {}; /* backoff durations, indexed by href; used to rate-limit connect attempts. */
var ackSeq = 0;            /* sequence number for ack tokens enerated by this process */
var nonceSeq = 0;          /* sequence number for nonces generated by this process */

exports.Connection = Connection530;
const Connection = Connection530;

/**
 * API. Create a DCP Connection object. This object could represent either the initiator or target end of a
 * connection, until it is specialized by either invoking the connect() or accept() methods. (connect
 * can also be invoked indirectly by send, request, keepalive, etc).
 *
 * This class inherits from EventEmitter and Reference.
 *
 * @note        Connection objects exist for the lifetime of a given DCP connection
 *              (session), whether or not the underlying transport (eg internet protocol) is connected or
 *              not. Once the DCP session has ended, this object has no purpose and is not reusable.
 *
 * @param {object} targetDescriptor   Object (usually a dcpConfig fragment) describing the target.
 *                                    This object may contain the following properties; 'location' is
 *                                    mandatory:
 *                                    - location: a URL or DcpURL that is valid from the Internet
 *                                    - friendLocation: a DcpURL that is valid from an intranet; if
 *                                      both location and friendLocation specified, the best one will
 *                                      be chosen by examining IP addresses (assuming an IP bearer).
 *                                    - identity: an object with an address property which is a promise
 *                                      that resolves to an instance of wallet.Address which represents
 *                                      to the target's identity; this overrides the initiator's
 *                                      identity cache unless options.strict is truey.
 *
 * @param {object} [options]        Extra connection options that aren't defined via dcpConfig.dcp.connectionOptions.
 *                                  These options include:
 *                                  - identityUnlockTimeout: Number of (floating-point) seconds to leave
 *                                                           the identity keystore unlocked between
 *                                                           invocations of Connection.send
 *
 * @returns instance of Connection that is specific to a target URL but not a role
 *
 * @emits Connection<ack>                - argument is network form Ack; when first transmitted
 * @emits Connection<send>               - argument is network form DcpMessage; when first transmitted
 * @emits Connection<beforeSend>         - argument is DcpMessage just before it is turned into network
 *                                         form for transmission
 * @emits Connection<sent>               - argument is DcpMessage whose receipt has been acknowledged by
 *                                         the peer. This includes messages inside batches.
 * @emits Connection<recvResponse>       - API track, arguments are [Response, Response]; when Response
 *                                         received
 * @emits Connection<connectionProgress> - API
 * @emits Connection<ready>              - API
 * @emits Connection<session>            - API
 * @emits Connection<end>                - API
 * @emits Connection<close>              - API
 * @emits Connection<connect>            - API
 * @emits Connection<disconnect>         - API
 * @emits Connection<request>            - API
 * @emits Connection<error>              - API
 */
function Connection530(targetDescriptor, options)
{
  const that = this;
  assert(targetDescriptor);
  assert(targetDescriptor.location);

  this.seq = Connection.seq = (Connection.seq || 0) + 1;
  this.state = new Synchronizer('initial', [
    'initial',
    'connecting',     /* session is working to negotiate dcpsid */
    'established',    /* session has been fully established, have dcpsid */
    'disconnected',   /* session is still valid, but underlying transport is no longer connected */
    'closing',        /* session is still valid, trying to close, rejecting new messages at API boundary */
    'closed'
  ]);
  if (debugging())
    this.state.on('change', (neu, old) => console.debug(this.debugLabel, chalk.green(`changed state from ${old} to ${neu}`)));

  const debugLabelInner = `#${Connection.seq}:`;
  this.debugLabel = chalk.grey(`-${debugLabelInner}`.padStart(8 - String(Connection.seq).length, ' '));
  this.role = new Synchronizer('unspecified', [ 'unspecified', 'initiator', 'target' ]);
  this.role.on('change', () => {
    const colour = this.role.is('target') ? chalk.yellow : chalk.grey;
    this.debugLabel = colour(`${String(this.role)[0]}${debugLabelInner}`.padStart(7 - String(Connection.seq).length, ' '));
    debugging('connection') && console.debug(this.debugLabel, 'assumed role ' + this.role);
  });

  this.identity = false;
  function memoizeIdentity(id)
  {
    that.identity = id;
    debugging('connection') && console.debug(that.debugLabel, 'connection owner is', that.identity?.address);
    if (!(that.identity instanceof wallet.Keystore || that.identity instanceof wallet.PrivateKey))
      throw new DCPError('unable to determine identity', 'DCPC-1018');
  }
  if (options?.identity && !(options.identity instanceof Promise))
    memoizeIdentity(options.identity);
  else
  {
    this.identityPromise = options?.identity || wallet.getId();
    this.identityPromise.then(memoizeIdentity);
  }

  this.targetDescriptor = Object.assign({}, targetDescriptor);
  if (!DcpURL.isURL(targetDescriptor.location))
    throw new Error('bad targetDescriptor: could not determine target location');

  this.options = leafMerge(
    ({ /* hardcoded defaults insulate us from missing web config */
      connectBackoff:        { mult: 0.5, base: 2, max: 30 }, /* see Backoff constructor */
      connectTimeout:        90,    /* how long an initiator will try to connect to a target */
      reconnectTimeout:      600,   /* how long an initiator will try to reconnect to a target */
      closeTimeout:          7,     /* how long we can wait for our close message to get sent */
      maxRTT:                120,   /* how long we can wait an ack. @todo: calculate reasonable default during connect() /wg feb 2023 */
      validitySlopTime:      600,   /* how much tolerance we have for messages outside the validity window */
      allowBatch:            true,
      maxMessagesPerBatch:   20,
      targetBatchSize:       100e6,
      maxBatchSize:          200e6,
      batchWaitTime:         0.03,
      identityUnlockTimeout: 300,
      ttl: {
        min: 15,
        max: 600,
        default: 120
      },
      transports: [ 'socketio' ],
      maxTransmitAttempts: 5,
    }),
    dcpConfig.dcp.connectionOptions.default,
    dcpConfig.dcp.connectionOptions[this.targetDescriptor.location.hostname],
    dcpConfig.dcp.connectionOptions[this.targetDescriptor.location.origin],
    options
  );

  if (usingDebugger && !dcpEnv.getenv('DCP_NETWORK_TIMEOUTS_DEBUG'))
  {
    /* short timeouts and debuggers don't get along well */
    this.options.connectTimeout         *= 10;
    this.options.reconnectTimeout       *= 10;
    this.options.lingerTimeout          *= 20;
    this.options.closeTimeout           *= 50;
    this.options.identityUnlockTimeout  *= 10;
  }

  this.dcpMessageRegistry = new DialogueRegistry(this);
  optionsSanityCheck(this.options);

  this.peerAddress = undefined;         /* peer's identity, after first message received */
  this.peerVersion = undefined;         /* protocol version that the peer is using (after state -> established) */
  this.hasNtp = undefined;              /* true => time is managed on this host by NTP */
  this.clockInfo = undefined;           /* Information about delta from remote clock */
  this.dcpsid = undefined;              /* Current session id (after state -> established) */
  this.closeErrorCode = undefined;      /* Error code associated with this connection's shutdown */
  this.ackSignPromise = undefined;      /* Promise that is held while signing an Ack - used only to delay shutdown */
  this.connectingPromiseHnd = undefined;/* Promise that is held for the duration of a connect() operation */

  this.txQueue = []             /* DcpMessages that are ready for transmission to peer */
  this.lastRxMessageId = null;  /* last DcpMessage.id received from peer, used for dup detection */
  this.lastRxAckToken = null;   /* last Ack.token received from peer, used for dup detection */
  this.lastTxNonce = null;      /* last DcpMessage.nonce we sent to the peer */
  this.lastRxNonce = null;      /* last DcpMessage.nonce we received from the peer, used by DcpMessage class */
  this.lastTxAckSealed = null;  /* last Ack we sent to the peer (sealed message) */
  this.lastTxAck = null;        /* used only in DCP-3957 bug workaround */
  this.transport = undefined;   /* handle for the active underlying transport */

  this.Ack          = ackCtorFactory(this);
  this.Request      = requestCtorFactory(this);
  this.Response     = responseCtorFactory(this);
  this.Notification = notificationCtorFactory(this);
  this.Batch        = batchCtorFactory(this);
  this.ErrorPayload = errorPayloadCtorFactory(this);

  /* Initialize the EventEmitter and Reference internals so that we can safely seal the object. */
  this.removeAllListeners();
  this.on('error', (error) => { debugging() && console.debug(this.debugLabel, 'event:', error)}); /* side-effect; avoid emit->throw lunacy */
  Reference.call(this, debugging, () => this.debugLabel);
  this.unref();

  if (debugging())
    this.on('ready', () => console.debug(this.debugLabel, 'established new session', this.dcpsid));

  /* Register an end handler which detects that the event fired so quickly that no useful work could
   * have been done. This is used to prevent client applications from accidentally cycling a ctor->error
   * loop so quickly that they consume all CPU.
   */
  this.on('end', function firstTickEndHandler() {
    if (that.__firstTick)
      throw new DCPError(`connection end event fired on same tick as constructor (${this.state}) for connection `
                         + this.targetDescriptor.location.href, 'DCPC-1027');
    that.off('end', firstTickEndHandler);
  });
  this.__firstTick = true;
  process.nextTick(() => (this.__firstTick = false));

  const __emit = this.emit;
  this.emit = function emitErrorWrapper(eventName) {
    try
    {
      __emit.apply(this, arguments);
    }
    catch(error)
    {
      if (eventName === 'error')
        console.error('Error handler threw error:', error);
      else
        this.emit('error', error);
      throw error;
    }
  }

  /* Add events debugging by intercepting this.emit */
  if (debugging())
  {
    const inspect = (dcpEnv.platform === 'nodejs') ? requireNative('util').inspect.custom : Symbol('inspect');
    const normalEmit = this.emit;
    this.emit = function emitDebugWrapper() {
      var disp;
      if (arguments[1] === undefined)
        disp = '';
      else if (arguments[1] instanceof URL)
        disp = arguments[1].href;
      else if (arguments[1][inspect])
        disp = arguments[1];
      else
        disp = Array.from(arguments).slice(1);

      debugging('events') && console.debug(this.debugLabel, chalk.cyan('fired event: ' + arguments[0]), disp);
      normalEmit.apply(this, arguments);
    };
  }

  /* Seal this object and the ctor to detect unexpected new properties later. Ctor seal is delayed to
   * end of first instanciation because super class ctors (eg Reference) might need to add static props.
   */
  Object.seal(this);
  if (Connection.seq === 1)
    Object.seal(Connection);
}

Connection.prototype = new EventEmitter();
Object.assign(Connection.prototype, Reference.prototype);
Connection.prototype.constructor = Connection;
Connection.versionInfo = versionInfo;             /* API. Version support info. Okay for tests to mutate props. */
Connection.seq = 0;                               /* sequence number for connections created by this process */

/* Add fancy console logging for Connection objects in nodejs */
if (dcpEnv.platform === 'nodejs')
{
  const inspect = requireNative('util').inspect.custom;
  Connection.prototype[inspect] = function Connection530$$inspect() {
    const ip = this.transport?.networkAddress || '<no transport>';
    return `[Object Connection(${String(this.role)[0]}#${this.seq}:${this.state}) ${this.targetDescriptor.location.pathname} `
      + `${String(this.peerAddress || '(no peer)').slice(0,10)} ${ip}]`;
  };
}

/**
 * Returns the current time according to the connect time keeper; used to calculate message validity.
 * @todo implement this
 */
Connection.prototype.currentTime = function Connection530$$currentTime()
{
  if (this.hasNtp || !this.clockInfo)
    return Date.now() / 1e3;

  assert(this.role.is('initiator'));
  return this.clockInfo.startTime + (performance.now() / 1000 - this.clockInfo.startNow) + this.clockInfo.halfRTT;
}

/**
 * Shutdown the connection. Not API.
 * - stop allowing incoming requests
 * - notify the other end we are closing (best effort)
 * - close the underlying transport
 * - reject all outstanding dialogue promises (results of Connection::send)
 *
 * @param {object} closeError    instance of Error; used for promise rejectinos
 * @param {any}    immediate     when truey skip remote notification - should be used sparingly, as it
 *                               causes the peer to keep memory allocated for a long time.
 *
 * @emits 'end'    the first time its called for a given Connection
 */
Connection.prototype.shutdown = function Connection530$$shutdown(closeError, immediate)
{
  const that = this;

  if (this.state.in(['closing', 'closed']))
    return;

  if ((this.state.is('disconnected') && this.role.is('target') || this.role.is('unspecified')))
    immediate = true;

  debugging('connection') && console.debug(this.debugLabel, 'shutting down connection:',
                                           `${closeError?.code || closeError?.message || closeError},`,
                                           'immediate =', Boolean(immediate));
  assert(closeError instanceof Error);
  this.closeErrorCode = closeError.code || 'EPIPE';
  if (this.state.is('initial'))
    this.__firstTick = false; /* skip warning if all we did was instantiate but not connect; likely in early error handler */
  this.state.set(['initial', 'connecting', 'established', 'disconnected'], 'closing');
  this.emit('end');

  if (immediate)
    setImmediate(() => finalize()); /* delay just long enough to fire pending events */
  else
  {
    /* Try to let Connection become quiescent before closing it. This means we don't miss out on sending,
     * for example, Responses which have already been computed but haven't quite made it out the door. It
     * also means that the peer will receive our close Request (assuming we're sending one), which is
     * helpful from a memory management POV in long-lived services.
     *
     * In the case where this function is being called because the peer has sent us a close Request, we
     * don't bother sending a close Request (the Response is sent elsewhere). In the case where we have
     * a disconnected session that is reconnectable, we reconnect it to deliver the close Request. This
     * is tricky because wedon't want to transition back to the established state (or backoff-sleep
     * etc), so we can't use the regular connect() API; instead we call connectTransport() and related
     * bits directly.
     */
    const shouldSendCloseRequest = closeError.code !== 'ECONNRESET';
    const shouldReconnectToClose = shouldSendCloseRequest
          && !immediate && Boolean(this.dcpsid) && this.state.is('disconnected');
    const shouldWaitForDialogues = shouldReconnectToClose && this.transport;

    debugging('connection') && console.debug(this.debugLabel, 'shutdown tasks:',
                  shouldSendCloseRequest ? 'will' : "won't", 'send close request,',
                  shouldReconnectToClose ? 'will' : "won't", 'reconnect to close');

    function a$reconnectToClose()
    {
      debugging('connection') && console.debug(that.debugLabel, 'reconnecting to deliver close request');
      const connectPromise = that.connectingPromiseHnd?.promise || that.connectTransport();
      return connectPromise.then(function reconnectToCloseInternal() {
        if (that.state.is('closed'))
          return;
        if (that.txQueue.onDeck)
          that.transmitOnDeckMessage();
        else if (that.lastTxAckSealed)
          that.retransmitAck();
        that.requestTxQueueService();
      }).catch(error => 'dont care');
    }

    function a$waitForDialogues()
    {
      const dlgPs = that.dcpMessageRegistry.getDialoguePromises();
      debugging('connection') && console.debug(that.debugLabel, 'waiting for', dlgPs.length, 'dialogue promises to settle');
      return Promise.allSettled(dlgPs);
    }

    function a$sendCloseRequest()
    {
      debugging('connection') && console.debug(that.debugLabel, 'enqueuing close request');
      const closeRequest = new that.Request('close', closeError.code).unref();
      return closeRequest.send().catch(error => 'dont care');
    }

    const ps = [ this.ackSignPromise ];
    if (shouldReconnectToClose || this.transport)
    {
      if (shouldWaitForDialogues) ps.push(a$waitForDialogues());
      if (shouldReconnectToClose) ps.push(a$reconnectToClose());
      if (shouldSendCloseRequest) ps.push(a$sendCloseRequest());
    }

    setTimeout(finalize, this.options.closeTimeout * 1e3).unref(); /* in case promises take forever to settle */
    Promise.all(ps).then(() => {
      if (this.ackSignPromise) /* new one made by activity in Promise.all(ps) */
        this.ackSignPromise.then(finalize);
      else
        finalize();
    });
  }

  /**
   * Finalize the Connection; transitions from closing to close state.
   * When this happens, the 'close' event is emitted, all messages in the registry have their
   * dialogue promises rejected, and all event handlers are unregistered.
   *
   * It is safe to call this function more than once.  After the first call, it is a noop.
   *
   * @emits Connection<close>
   */
  function finalize()
  {
    if (that.state.is('closed'))
      return;

    that.state.set('closing', 'closed');
    debugging('connection') && console.debug(that.debugLabel, 'finalizing connection');
    if (that.transport)
    {
      const transport = that.transport;
      that.transport = false;
      transport.close();
    }
    clearTimeout(that.txQueue.onDeck?.rttTimerHnd);
    if (that.connectingPromiseHnd)
      that.connectingPromiseHnd.reject(closeError); /* suspenders - belt in connectTransport() */
    if (that.txQueue.onDeck?.ackWait?.reject)
      that.txQueue.onDeck.ackWait.reject(closeError);
    that.dcpMessageRegistry.clear(closeError);
    that.unref();
    that.state.removeAllListeners();
    that.role.removeAllListeners();
    that.emit('close');
    that.removeAllListeners();
    that.on('error', (error) => { debugging() && console.debug(this.debugLabel, 'event:', error)}); /* side-effect; avoid emit->throw lunacy */
    debugging('connection') && console.debug(that.debugLabel, 'connection is closed');
  }
}

/**
 * API. Close the connection.
 */
Connection.prototype.close = function Connection530$$close()
{
  debugging() && console.debug(this.debugLabel, `closing connection to ${this.targetDescriptor.location.href} (API call)`);
  this.shutdown(new DCPError('connection closed', 'EPIPE'));
}

/**
 * Transmit a sealedMessage to the peer via the current transport.
 */
Connection.prototype.transmit = function Connection530$$transmit(sealedMessage)
{
  assert(typeof sealedMessage === 'string');
  if (this.state.is('closed'))
  {
    debugging('connection') && console.debug(this.debugLabel, 'connection is closed; cannot transmit', sealedMessage.slice(0,500) + '...');
    return;
  }

  if (!this.transport)
  {
    debugging('connection') && console.debug(this.debugLabel, 'not transmitting message; no transport'); /* re-transmit should fix this */
    return;
  }

  debugging('wire') && wireLog(this.debugLabel, sealedMessage, 'tx');
  this.transport.send(sealedMessage);
}

/**
 * Transmit the onDeck message over the current transport. If the message has not been Ack'd after
 * maxRTT, we close the current transport (assuming it hasn't already been cycling) to engage the
 * reconnect logic.
 *
 * The reconnect logic can call this message multiple times; each time it is called, the maxRTT timer
 * is reset. The maxRTT timer is also cleared as soon as the message gets Ack'd.
 *
 * @note treat as infallible
 */
Connection.prototype.transmitOnDeckMessage = function Connection530$$transmitonDeckMessage()
{
  /* If onDeck is defined but sealedMessage is not, we are in the await to sign the message; this means
   * that we are going to call this function again as soon as it resolves, so we don't need to do
   * anything special to handle this situation.
   */
  if (!this.txQueue.onDeck.sealedMessage)
    return;

  /* serviceTxQueue has a couple of opportunties for races that can cause the same message to get sent to
   * the peer more than once. That's not a real problem because of dup-detection, but we can simple drop
   * the extra transmission if we know the transport is reliable (eg it is built on TCP), and more
   * expedient that making serviceTxQueue completely race-free.
   */
  if (this.txQueue.onDeck.transport === this.transport && this.transport.reliable)
  {
    debugging('connection') && console.debug(this.debugLabel, 'ignoring message retransmission request - reliable transport');
    return;
  }
  this.txQueue.onDeck.transport = this.transport;

  /* rttTimerHnd measures the time between DcpMessage tx->Ack rx. If it is too long, we decide that the
   * underlying transport connection must be broken, and close it to trigger reconnect.
   */
  if (this.txQueue.onDeck.rttTimerHnd)
  {
    debugging('connection') && console.debug(this.debugLabel, 'clearing existing RTT timer');
    clearTimeout(this.txQueue.onDeck.rttTimerHnd);
  }
  this.txQueue.onDeck.rttTimerHnd = setTimeout(() => {
    debugging() && console.debug(this.debugLabel, 'max RTT exceeded for message', this.txQueue.onDeck.message, '- disconnecting transport', this.transport);
    this.txQueue.onDeck.transport = false;
    if (this.transport)
    {
      this.transport.close();
      this.hangupHandler(this.transport);
    }
  }, this.options.maxRTT * 1e3).unref();

  /* Send the message to the peer */
  if (this.txQueue.onDeck.tries++ > this.options.maxTransmissionAttempts)
    this.shutdown(new DCPError('maximum transmission attempts exceeded', 'EINVAL'), true); /* rejects ackWait */
  this.transmit(this.txQueue.onDeck.sealedMessage);
  if (this.txQueue.onDeck.tries === 1)
    this.emit('send', this.txQueue.onDeck.message);
}

/**
 * API. Accept a new incoming connection from an initiator. Normally invoked by the Target class.
 */
Connection.prototype.accept = function Connection530$$accept(transport)
{
  if (!this.identity)
    throw new Error('target identity must be resolved before accepting initiator connection');

  this.role.set('unspecified', 'target');
  this.hasNtp = true;
  this.txQueue.length = 0;
  this.state.set('initial', 'connecting');
  this.attach(transport);
}

/**
 * Attach the specified transport to the connection. Closes any old transport already attached to the
 * connection; attaches the events required to use this transport to right connection methods.
 *
 * @note treat as infallible
 */
Connection.prototype.attach = function Connection530$$attach(transport)
{
  if (this.transport && this.transport !== transport)
    this.transport.close();
  assert(!this.state.in(['initial', 'closed']));

  transport.on('message',        (m) => this.receive(m));
  transport.on('end',            ()  => this.hangupHandler(transport));
  transport.on('close',          ()  => this.hangupHandler(transport));
  this.transport = transport;
  debugging('connection') && console.debug(this.debugLabel, 'attached events from transport', transport, 'to connection', this);
}

/**
 * API. Establish a link from the initiator to the target via the underlying transport, and
 * start or resume a DCP session.
 *
 * This function can be called from multiple "threads", but must never call connectTransport from
 * multiple "threads". It must also return the same promise for every call, if that promise was not
 * settled when the function was entered.
 *
 * Note that this function can become reentrant due to the hangupHandler getting called if the
 * underlying transport hangs up before session establishment.
 */
Connection.prototype.connect = function Connection530$$connect()
{
  const connectCallerStack = new Error('connect stack memo').stack;
  assert(!this.role.is('target'));

  if (this.state.in(['closing', 'closed']))
    throw new DCPError('Connection already ' + this.state, this.closeErrorCode);
  if (this.state.is('initial'))
    this.role.set('unspecified', 'initiator');
  if (this.connectingPromiseHnd)
    return this.connectingPromiseHnd.promise;

  /* Promise with memoized resolve/reject accessors lets us reuse the promise in the connectToClose
   * portion of the shutdown code; the wrappers create better error stacks.
   */
  assert(!this.connectingPromiseHnd);
  const connectingPromiseHnd = this.connectingPromiseHnd = {};
  connectingPromiseHnd.promise = new Promise((resolve, reject) => {
    connectingPromiseHnd.resolve = resolve;
    connectingPromiseHnd.reject  = reject;
  });

  if (this.state.is('established'))
    connectingPromiseHnd.resolve(); /* late+redundant call from application => nop */
  else
  {
    debugging('connection') && console.debug(this.debugLabel, `connecting to ${this.targetDescriptor.location}`);
    this.state.set(['initial', 'disconnected'], 'connecting');

    this.connectInternal()
      .then(() => {
        assert(this.connectingPromiseHnd === connectingPromiseHnd);
        this.connectingPromiseHnd = false;
        connectingPromiseHnd.resolve();
      })
      .catch(error => {
        assert(this.connectingPromiseHnd === connectingPromiseHnd);
        this.connectingPromiseHnd = false;
        debugging() && console.debug(this.debugLabel, 'giving up on connection;', error);
        error.stack += connectCallerStack.replace(/^.*stack memo.*\n/, '\n    -- connect -----------\n');
        connectingPromiseHnd.reject(error);
        this.shutdown(error);
      })
      .finally(() => {
        assert(this.connectingPromiseHnd === false);
      });
  }

  return this.connectingPromiseHnd.promise;
}

/**
 * Internal part of connect() expressed as an async function to keep connect() clean with respect to
 * always returning only one promise.
 */
Connection.prototype.connectInternal = async function Connection530$$connectInternal()
{
  const that = this;
  assert(!this.dcpsid || this.lastTxAckSealed);

  /* states we can we be in here:
   * 1 - new connection: no dcpsid, nothing on deck         -> send connect message and negotiateSession()
   * 2 - new connection: no dcpsid, connect message on deck -> send connect message and negotiateSession()
   * 3 - reconnect: have dcpsid, something on deck          -> resumeSession(), onDeck retransmit might be triggered by old-Ack receipt
   * 4 - reconnect: have dcpsid, nothing on deck            -> resumeSession()
   *
   * - negotiateSession() transitions from connecting -> established
   * - resumeSession() transitions from disconnected|connecting -> established
   */

  /* Async Notes:
   * - Must not await before connectRequest enqueued to preserve correct txQueue order when Connection::send initiates
   * - Can suddenly experience closed transition without warning
   * - this.connectingPromiseHnd.reject best handled by throwing in here
   * - connectTransport bails without error when closed connection is discovered
   */
  if (!this.dcpsid && !this.txQueue.onDeck)           /* 1 */
  {
    assert(this.txQueue.length === 0);

    /* Create the connect request and enqueue it before any await happens. At the bottom of this
     * function, we request a queue service which puts that message on deck and hopefully transmits it.
     * Eventually, the connect request's dialogue promise is resolved with the connection response. This
     * triggers session negotiation, which verifies version compatibility and sets the dcpsid.
     *
     * Initiator          Target
     * ---------------    ----------------
     * connect request ->                        [create sid, establish session from target POV]
     *                 <- ack
     *                 <- connect response       [session established from initiator POV]
     *             ack ->
     *
     * Since ack retransmission is used to resume sessions, session initiation can be problematic in the
     * case where the network connection is interrupted before the initiator receives the connect
     * response. For this reason, we retransmit the connect request (on deck message) every time the
     * network layer connects after the initial queue service.
     */
    let connectRequest = new this.Request('connect', { version: versionInfo.implementation, sid: randStr21() });

    this.a$enqueue(connectRequest).then(connectResponse => {
      this.negotiateSession(connectRequest, connectResponse);
      this.off('connect', retransmitConnectRequest);
    }).catch(error => this.shutdown(error, true));

    function retransmitConnectRequest()
    {
      if (!that.txQueue.onDeck)
        return;
      assert(that.txQueue.onDeck.message === connectRequest);
      that.transmitOnDeckMessage(); /* try to resolve negotiateSession() promise from 1. */
    }
    this.on('connect', retransmitConnectRequest);

    if (this.identityPromise)
      await this.identityPromise;
    await this.connectTransport();
  }
  else if (!this.dcpsid && this.txQueue.onDeck)       /* 2 */
  {
    assert(this.txQueue.onDeck.message.payload.operation === 'connect' && this.txQueue.onDeck.message instanceof this.Request)
    await this.connectTransport();
  }
  else
  {
    assert(this.dcpsid);
    await this.connectTransport();
    if (!this.state.is('closed'))
      this.resumeSession(); /* -> established */      /* 3, 4 */
  }

  this.requestTxQueueService();
}

/**
 * The nuts and bolts of Initiator connection happens in here, except for state transistion and
 * operations like requesting queue service and resuming the session. This allows us to reuse this
 * component to re-initiate disconnected sessions after the session has transitioned to the closing
 * state to drop off the close request message without transitioning through the established state
 * first.
 *
 * The basic strategy is to sleep for a brief period of time and then run through connection attempts
 * for each configured transport. We do this in a loop until the timer expires or the transport manages
 * to establish a transport-layer connection.  Once that happens, we transmit whatever message is on
 * deck -- this will be the connect message for fresh Connections.
 *
 * If the transport hangs up after it establishes but before everything happens, we should get back
 * back here for by virtue of the session reconnection logic.
 *
 * It is very important that connectTransport() is entered only on one "thread", and callers must
 * have there own guards to ensure this. Currently, connectTransport is entered in two places:
 * - connect()
 * - shutdown()
 *
 * Connect only calls connectTransport() from the connecting state.
 * Shutdown only calls connectTransport() after transitioning to the closing state.
 */
Connection.prototype.connectTransport = async function Connection530$$connectTransport()
{
  var sleep, lastError;

  assert(!this.state.is('closed'));
  if (this.role.is('initiator'))
    assert(this.state.in(['connecting', 'closing']))
  else
    assert(this.state.is('connecting'));

  const connectTimeout = !this.dcpsid ? this.options.connectTimeout : this.options.reconnectTimeout;
  const maxBackoffInterval = Math.min(this.options.connectBackoff.max, (connectTimeout / 2) - 1);
  const backoff = globalBackoffs[this.targetDescriptor.location.href]
                = globalBackoffs[this.targetDescriptor.location.href] || new Backoff(this.options.connectBackoff, maxBackoffInterval);
  const establishByNow = performance.now() + (connectTimeout * 1e3);

  /* We sleep before each connection attempt. This timer interrupts that sleep upon connect timeout. */
  const connectTimerHnd = setTimeout(() => sleep && sleep.intr(), connectTimeout).unref();

  while (!this.state.is('closed') && performance.now() < establishByNow)
  {
    await (sleep = a$sleep(backoff.next()));
    if (this.state.is('closed'))
      break;

    try
    {
      if (await this.enlistTransport())
        break; /* true => transport attached */
    }
    catch(error)
    {
      lastError = error;
      if (error.name === 'ReferenceError' || error.name === 'SyntaxError' || error.name === 'TypeError'
          || error.code === 'EASSERT' || error.code === 'DCP_SYNCHRONIZER_ESYNC')
      {
        /* No point in retrying errors that reach here - they'll just happen again */
        debugging() && console.debug(this.debugLabel, 'Permanent failure, cannot establish DCP session'
                                     + `${this.transport ? ' over ' + this.transport.name : ''}:`, error.message);
        break;
      }

      /* This awkward section can swallow errors, because we consider mystery errors to be the
       * result of unknown transport errors, like maybe DNS timeout something, so we try to hit them a
       * few (maxConnectTries) times before reporting the last one.
       */
      debugging('connection') && console.debug(this.debugLabel, 'Could not establish DCP session'
                                               + `${this.transport ? ' over ' + this.transport.name : '.'}`
                                               + ' (will try again)', error);
    }
  }

  /* Connect loop finished: either we have a transport connection, or we don't and have probably timed out. */
  clearTimeout(connectTimerHnd);

  if (this.state.is('closed'))
  {
    debugging() && console.debug(this.debugLabel, 'connection closed before transport connected');
    return;
  }
  if (!this.transport)
  {
    debugging() && console.debug(this.debugLabel, `could not connect; now=${performance.now()}; establishByNow=${establishByNow}`);
    const error = lastError || new DCPError(`Could not connect to ${this.targetDescriptor.location} within ${connectTimeout}s`, 'ETIMEDOUT');
    this.shutdown(error);
    throw error;
  }

  debugging() && console.debug(this.debugLabel, 'established underlying', this.transport.name, 'transport connection');
  this.emit('connect', this.targetDescriptor.location); /* UI hint: internet available" */
  backoff.reset();

  /* Now have a transport-layer connection.
   * - If this is a connect operation, the calling code will send a connect request.
   * - If this is a reconnect, the calling code will retransmit the last Ack, which can in turn
   *   trigger a retransmission of the peer's last DcpMessage.
   */
}

/**
 * Send the last Ack we sent. This should be a one-line statement in the code, but DCP-3957 adds some
 * frustrating extra requirements. When that bug is fixed, the callsite should be changed so that we
 * don't need this function anymore.
 */
Connection.prototype.retransmitAck = function Connection530$$retransmitAck()
{
  if (this.lastTxAck.dcpsid)
    this.transmit(this.lastTxAckSealed);
  else
  {
    /* Horrifying workaround for DCP-3957: forge and transmit a new Ack that has the dcpsid this one lacks */
    if (!this.dcpsid)
      this.shutdown(new DCPError('session is unresumable', 'DCP-3957'));
    const fakeMessageBody = { id: this.lastTxAck.messageId, ackToken: this.lastTxAck.token };
    this.sendAck(fakeMessageBody, this.lastTxNonce);
  }
}

/**
 * Transition back to established and pump the transport with a keepalive. MaxRTT on the keepalive
 * message is responsible for ensuring that packets are moving across the wire and killing the
 * transport connection if not so that we can resume the reconnect attempt.  The reconnectTimeout is
 * responsible for ensuring that we either eventually resume the session or end it.
 *
 * @param {string} sealedMessage   see Connection::receive
 * @param {object} [signedMessage] see Connection::receive
 */
Connection.prototype.resumeSession = function Connection530$$resumeSession(incomingSealedMessage, incomingSignedMessage)
{
  debugging('connection') && console.debug(this.debugLabel, 'reconnected session with new transport instance');

  this.state.set(['connecting', 'disconnected'], 'established');
  if (this.lastTxAckSealed)
    this.retransmitAck();
  if (incomingSealedMessage)
    this.receive(incomingSealedMessage, incomingSignedMessage);
  if (this.state.is('closing'))
    return;

  const request = new this.Request({ operation: 'keepalive' }).unref();
  request.send().catch(async (error) => {
    await a$sleep(0.5 + Math.random()); /* slow down reconnect->crash loops */
    if (error.code === 'EASSERT' || error.code === 'DCP_SYNCHRONIZER_ESYNC')
      throw error;
    debugging() && console.debug(this.debugLabel, 'ignoring reconnect-keepalive error:', error.code || error.message);
  });
}

/**
 * Establish a transport-level connection to the peer. Iterate through all the known transports, in
 * order, resolving the promise as soon as one connects. In some circumstances ("permanent failure"), we
 * remove the transport from this Connection's configuration, so that we never try it again. That
 * configuration is shallow-cloned during construction, so that a new instance of Connection will get
 * the full list.
 *
 * @emits   Connection<connectionProgress> event to let the UI know that we're trying to connect
 *
 * @returns {Promise} that
 *          - resolves true if the transport was attached, or false when none of the transports could
 *            establish a (transport-level) connection.
 *          - rejects with an instance of Error when this instance of Connection will never connect to
 *            the Target. Once such case has code DCPC-1014, which usually happens when all configured
 *            transports report permanent failure.
 */
Connection.prototype.enlistTransport = async function Connection530$$enlistTransport()
{
  assert(this.state.in(['connecting', 'closing']));
  assert(this.role.is('initiator'));

  const availableTransports = this.options.transports.slice();
  if (availableTransports.length === 0)
    throw new DCPError('no transports defined', 'DCPC-1014');

  for (let transportName of availableTransports)
  {
    let errorCode;

    debugging('connection') && console.debug(this.debugLabel, 'trying transport', transportName);
    try
    {
      this.emit('connectionProgress', { begin: transportName }); /* UI hint */
      const transportModule = require('./transports/' + transportName);
      const transport = new transportModule.TransportClass(this.targetDescriptor, this.options[transportName]);

      const transportPromiseHnd = {};
      transportPromiseHnd.promise = new Promise((resolve, reject) => {
        transportPromiseHnd.resolve = resolve;
        transportPromiseHnd.reject = reject;
      });

      transport.on('error',          (error) => transportPromiseHnd.reject(error));
      transport.on('connect-failed', (error) => transportPromiseHnd.resolve(error));
      transport.on('connect',        ()      => transportPromiseHnd.resolve(true));
      transport.on('end',            ()      => transportPromiseHnd.resolve(new DCPError('transport ended during establish', 'DCP_TRANSPORT_FAILURE_TRANSIENT')));
      transport.on('close',          ()      => transportPromiseHnd.resolve(new DCPError('transport closed during establish', 'DCP_TRANSPORT_FAILURE_TRANSIENT')));
      this.attach(transport);

      const etStatus = await transportPromiseHnd.promise;
      debugging('connection') && console.debug(this.debugLabel, 'transport', transportName,
                                               `status=${etStatus === true ? 'connected' : (etStatus.code || etStatus.message || etStatus)}`);
      if (etStatus === true)
        return true;

      /* Arriving here means this.transportPromiseHnd resolved with an error; => connect-failed event */
      transport.removeAllListeners();
      transport.close();
      this.transport = false;
      assert(etStatus instanceof Error);
      const error = etStatus;
      errorCode = error.code;
      this.emit('connectionProgress', { fail: transportName, code: errorCode, httpStatus: error.httpStatus })
    }
    catch(error)
    {
      debugging() && console.debug(this.debugLabel, `error connecting with ${transportName}:`, error);
      errorCode = error.code;
      this.emit('connectionProgress', { fail: transportName, error });
    }
    finally
    {
      a$sleep(0);  /* ensure we avoid a work->throw loop that never yields */
    }

    switch (errorCode)
    {
      default:
      case 'DCP_TRANSPORT_FAILURE_PERMANENT':
        debugging('connection') && console.debug(this.debugLabel, `${errorCode || 'Error'} connecting to ${this.targetDescriptor.location} with ${transportName} (won't try again)`);
        this.options.transports = this.options.transports.filter(name => name !== transportName);
        break;
      case 'DCP_TRANSPORT_FAILURE_TRANSIENT':
        debugging('connection') && console.debug(this.debugLabel, `${errorCode} connecting to ${this.targetDescriptor.location} with ${transportName} (will try again)`);
        break;
    }
  }

  return false; /* no transport could establish a connection */
}

/**
 * Negotiate a DCP session. Responds to an incoming connect request or response.
 *
 * @param {Request}    connectRequest     the DcpMessage that the initiator sent
 * @param {Response}   response           the DcpMessage that target responded with
 *
 * @treat as infallible
 */
Connection.prototype.negotiateSession = function Connection530$$negotiateSession(connectRequest, response)
{
  var shutdownReason;
  const that = this;

  assert(connectRequest instanceof this.Request);
  assert(response instanceof this.Response || (!response && this.role.is('target')));

  this.peerVersion = this.role.is('initiator') ? response.payload.version : connectRequest.payload.data.version;
  if (!this.peerVersion)
     shutdownReason = new DCPError('invalid connect message received from peer', 'EVERSION');
  if (!semver.satisfies(this.peerVersion, versionInfo.compatibility))
    shutdownReason = new DCPError(`version mismatch; peer is version ${this.peerVersion}; we support ${versionInfo.compatibility}`, 'EVERSION');

  /* Figure out the dcpsid for this session */
  if (this.role.is('target'))
  {
    /* Target: create the dcpsid and respond with it. */
    assert(this.txQueue.length === 0 && !this.txQueue.onDeck);

    this.dcpsid = connectRequest.payload.data.sid + randStr21();
    const crPayload = shutdownReason
          ? { message: versionInfo.compatibility, code: 'EVERSION' }
          : { version: versionInfo.implementation, operation: 'connect' };
    const connectResponse = new this.Response(connectRequest, crPayload);
    connectResponse.success = shutdownReason ? false : true;
    connectResponse.send().then(
      function sendResolved() {
        /* Initiator can fully utilize the session right away, while target needs to delay until the
         * initiator has acknowledged receipt of the connect response in this implementation. That is
         * to ensure we don't inadvertenly batch up a request that is triggered onsession with the
         * connect response, because that is prohibited on the wire and enforced in the initiator.
         * The 'ready' event indicates a target is ready for full utilization.
         */
        that.emit('ready', that);
      },
      function sendRejected(error) {
        debugging() && console.debug(that.debugLabel, 'could not send connect response;', error.code || error);
        that.shutdown(error);
      }
    );
  }
  else
  {
    /* Initiator: adopt the dcpsid in the response, even if it is invalid or something else is wrong.
     * It is safe here to adopt invalid dcpsid since we are adopting it without going to the established
     * state; i.e. not request will ever make it to the application layer.
     */
    if (response.success !== true)
      shutdownReason = new DCPError(`peer requires version ${response.payload.message} (${response.payload.code})`, 'EVERSION');
    else if (!response.dcpsid.startsWith(connectRequest.payload?.data?.sid))
      shutdownReason = new DCPError('peer proposed an invalid dcpsid', 'EDCPSID');

    this.dcpsid = response.dcpsid;

    // Initiator: check if the target's identity was unexpectedly changed
    let presharedPeerAddress;
    if (!this.options.strict && this.targetDescriptor.identity && this.secureLocation)
      presharedPeerAddress = this.targetDescriptor.identity.address;

    let idc = getGlobalIdentityCache();
    let noConflict = idc.learnIdentity(this.targetDescriptor.location, this.peerAddress, presharedPeerAddress);

    if (!noConflict)
      shutdownReason = new DCPError(`**** Security Error: Peer address ${this.peerAddress} does not match the saved address for ${this.targetDescriptor.location}`, 'EADDRCHANGE');
  }

  /* Shutdown if there was a version or identity problem */
  if (shutdownReason)
  {
    debugging() && console.debug(this.debugLabel, shutdownReason.message);
    this.shutdown(shutdownReason, true);
    return;
  }

  /* Start the session unless we've closed. We can close if we connect to a target and it closes the
   * connection before establishing the session, i.e. because it is shutting down.
   */
  if (!this.state.in(['closing', 'closed']))
  {
    this.state.set('connecting', 'established');
    /* Start the session. */
    this.emit('session', this);
    /* For initiators, emit the 'ready' event. */
    if (this.role.is('initiator'))
      this.emit('ready', this);
  }
};

/**
 * Push a DcpMessage onto the txQueue FIFO and request queue service. This routine is also responsible
 * for creating the promise that is returned from Connection::send, and creating the message registry
 * entry which is how we track these promises for eventual resolution/rejection based on other Acks or
 * DcpMessages from the peer.
 *
 * This function also slides wrapper around the Connection::send dialogue promise to provide an enhanced
 * stack trace, allowing the developer to both pinpoint the source of the message and the reason for
 * rejection from log messages.
 *
 * @param {object} message     instance of DcpMessage; the message we want to send
 * @returns {Promise} which
 *   - rejects with an instance of Error when the connection closes
 *   - resolves with the response from the peer
 */
Connection.prototype.a$enqueue = function Connection530$$a$enqueue(message)
{
  const promiseHnd = {};
  const sendCallerStack = new Error('send stack memo').stack;
  assert(message instanceof this.Response || message instanceof this.Request || message instanceof this.Notification);

  debugging('connection') && console.debug(this.debugLabel, 'enqueue', message);
  this.dcpMessageRegistry.add(message, promiseHnd, sendCallerStack);
  this.txQueue.push(message);

  if ((message.payload?.validity?.time === null) && (message instanceof this.Request || message instanceof this.Notification))
    message.payload.validity.time = this.currentTime();

  promiseHnd.promise = new Promise((resolve, reject) => {
    promiseHnd.resolve = resolve;
    promiseHnd.reject = function sendPromiseRejectWrapper(error) {
      /* Reject the send-promise with the usual semantics. We try to provide an enhanced stack for
       * better debugging, but this can be called more than once with the same instance of error,
       * so we take great pains to avoid mutating it.
       */
      const abortCallerStack = new Error('abort stack memo').stack;
      const eproto = Object.create(error);
      eproto.constructor = error.constructor;
      const errorCopy = Object.create(eproto);
      errorCopy.code    = error.code;
      errorCopy.message = error.message;
      errorCopy.stack   = error.stack;
      errorCopy.stack  += abortCallerStack.replace(/^.*stack memo.*\n/, '\n    -- abort -------------\n');
      errorCopy.stack  += sendCallerStack.replace(/^.*stack memo.*\n/, '\n    -- send --------------\n');
      reject(errorCopy);
    };
  });

  return promiseHnd.promise;
}

/**
 * API. Send a DcpMessage to the peer. If the session has not been established, or is disconnected, this
 * triggers a call to connect.
 *
 * @returns a promise which is resolved when the dialogue with the peer completes. A dialogue which
 *          starts with a Request is resolved when we receive the corresponding Response. A dialogue
 *          which starts with a Response or Notification is resolved when we receive the corresponding
 *          Ack. The promise is rejected with an instance of Error if the Connection closes during the
 *          dialogue. In the time between when the Connection emits 'end' and when it emits 'close', this
 *          function will attempt to send Responses, but will reject Requests immediately
 *
 * @todo consider removing arbitrary message transmission and restrict API Request / Response / Notification
 */
Connection.prototype.send = async function Connection530$$send(message)
{
  assert(arguments.length === 1);

  if (!(message instanceof this.Request) && !(message instanceof this.Response) && !(message instanceof this.Notification))
    message = new this.Request(message /* payload */)

  if (this.state.in(['initial', 'disconnected']) && !this.role.is('target'))
  {
    /* Must not await before call to connect to preserve txQueue order */ 
    this.connect().catch(error => {
      if (error.code !== 'EPIPE') /* EPIPE from close during connect will reject the message itself */
        this.emit('error', error);
    });
  }

  switch(String(this.state))
  {
    case 'closing':
      if (!(message instanceof this.Response) && (message.payload.operation !== 'close'))
        throw new DCPError('cannot send non-Response message while connection is closing', this.closeErrorCode);
      /* fallthrough */
    case 'disconnected':
    case 'connecting':
    case 'established':
    {
      const dialoguePromise = this.a$enqueue(message);
      this.requestTxQueueService();
      return dialoguePromise;
    }
    case 'initial':
    default:
      assert(false);
      /* fallthrough */
    case 'closed':
      /* Ensure we always yield so the client dev can't accidentally think their code is in a safe
       * connection-replacement message-retry loop when in fact we wind up staying on the same "thread"
       * because we don't always await here nor always return the message reference promise.
       */
      await a$sleep(0);
      throw new DCPError(`cannot send message on ${this.state} connection`, this.closeErrorCode);
  }

  throw new Error('unreached'); // eslint-disable-line no-unreachable
}

/**
 * API. Sugar which allows us to construct and transmit a request on the current connection in a single
 * statement.
 *
 * @param {string} operation
 * @param {object} data
 * @returns {Promise<Response>} a promise which resolves to the response sent by the peer
 */
Connection.prototype.request = function Connection530$$request(operation, data)
{
  const payload = { operation, data };
  const req = new this.Request(payload);
  return this.send(req);
}

/**
 * API. Sugar which allows us to construct and transmit a notification on the current connection in a
 * single statement.
 *
 * @param {object} data
 * @returns {Promise<Response>} a promise which resolves when the peer has acknowledged receipt of the
 *                              notification at the protocol level
 */
Connection.prototype.notify = function Connection530$$notify(data)
{
  const notification = new this.Notification(data);
  return this.send(notification);
}

/**
 * API. Send a keepalive Request to the peer; resolves when the Response arrives, or rejects
 * when connection closes.
 * @returns {Promise}
 */
Connection.prototype.keepalive = function Connection530$$keepalive()
{
  const request = new this.Request({ operation: 'keepalive', data: Date.now() });
  return request.send();
}

/**
 * Helper function that returns true when the connection has a pending transmission. This is only for
 * DcpMessages, not Acks, and includes both messages which have been enqueued and messages which have
 * not yet been acknowledged.
 */
Connection.prototype.hasPendingTx = function Connection530$$hasPendingTx()
{
  return this.txQueue.length !== 0 || Boolean(this.txQueue.onDeck);
}

/**
 * Helper function that returns true when the connection is quiescent - no messages
 * waiting resolution in the message registry.
 */
Connection.prototype.isQuiescent = function Connection530$$isQuiescent()
{
  return this.dcpMessageRegistry.isEmpty();
}

/**
 * Combine the current onDeck message and other messages which are in the txQueue into one batch
 * message, for cheaper transmission.
 *
 * This routine waits a small amount of time (in the spirit of Nagle's Algorithm) so that other messages
 * which are nearly-ready for transmission have the opportunity to get out with the current message.
 * Specific request payload target and absolute sizes are monitored during the creation of the batch,
 * to maximize utility without risking blowing up size string size limits for very large messages. In
 * order to avoid throwaway-serialization, the payload is transformed into jsonPayload while this is
 * happening; the inverse transformation happens transparently at the receiver's end.
 *
 * Once this batch is transmitted, a single Ack for the entire batch is sent back over the wire.
 */
Connection.prototype.combineToBatch = async function Connection530$$combineToBatch()
{
  var batchSize = 0;

  /* Prepare a message for batching. Mutates message and batchSize, returns network repn after
   * transforming message to jsonPayload format.
   */
  function prepareForBatch(message)
  {
    /* Mutate message to pre-serialize payload data as needed */
    if (message.payload?.data)
    {
      message.payload.jsonData = JSON.stringify(message.payload.data);
      delete message.payload.data;
    }

    if (message.payload?.jsonPayload)
      batchSize += message.payload.jsonPayload.length;

    return message.toNetwork();
  }

  /* Wait briefly for the queue to fill. If there is already a response message destined for the batch,
   * only wait a single tick. If there are no more messages in the transmit queue after the wait, we
   * don't bother creating a batch.
   */
  if (!this.state.is('closing'))
    await a$sleep(this.txQueue.onDeck.message.type === 'response' ? 0 : this.options.batchWaitTime);
  if (this.txQueue.length === 0)
    return;

  /* Swap out the on-deck message with a batch that includes it */
  const topMessage = this.txQueue.onDeck.message;
  const batch = [ prepareForBatch(topMessage) ];
  this.txQueue.onDeck.message = new this.Batch(batch);
  const entry = this.dcpMessageRegistry.add(this.txQueue.onDeck.message, false, new Error('batch stack memo').stack);
  entry.batch = [ topMessage ];

  /* Loop over the transmit queue, grabbing messages from the queue and adding to the batch,
   * provided our batch size (actually the sum of the payload.data stringified sizes) is under the
   * limit and we haven't go too many messages in the batch. We yield at the bottom of the loop to
   * let stragglers in and avoid starving the event loop. JSON stringification can be expensive for
   * some playloads, measured in the 100s of ms.
   */
  while (!this.state.is('closed') && this.txQueue.length && this.options.maxMessagesPerBatch > batch.length)
  {
    const message = this.txQueue[0];

    /* Don't add this message to the batch if we are already at our target batch size */
    if (!(batchSize <= this.options.targetBatchSize))
      break;

    const networkRepn = prepareForBatch(message); /* mutates message and batchSize */

    /* Don't add this message to the batch if it puts us over the batch size limit */
    if (!(batchSize < this.options.maxBatchSize))
      break;

    debugging('batch') && console.debug(this.debugLabel, 'added to batch:', message);
    batch.push(networkRepn);
    entry.batch.push(message);
    this.txQueue.shift();
    if (!this.state.is('closing'))
      await a$sleep(0);
  }
}

/**
 * Request that the transmit queue get serviced soon. Coalesces multiple requests, potentially from
 * multiple "threads" into a single request. Waits until it is okay to service the queue, then services
 * it.
 *
 * @note treat as infallible
 */
Connection.prototype.requestTxQueueService = function Connection530$$requestTxQueueService()
{
  var ps = [];

  if (this.txQueue.pendingService || this.state.is('closed'))
    return;

  if (!this.identity)
    ps.push(this.identityPromise);                /* Wait until our identity has been established */
  ps.push(this.txQueue.onDeck?.ackWait?.promise); /* Wait until the previous DcpMessage has been ack'd */

  if (!this.dcpsid)
  {
    /**
     * There's an interesting race which can happen during session negotiation; we can process the Ack
     * from the connect response before we have finished establishing the session. Rather than trying to
     * force-order message processing, we instead refuse to pump the queue unless the top message is a
     * message which is permitted before session establishent. If that's not the case, we just wait for
     * the session to start up, emit the 'ready' event and pump it then.
     *
     * We are always certain the (permitted) connect request is the top message for Initiators on the
     * first queue service because we always enqueue it in connect or send before before any await
     * statements or event emission. This implies that if the top message isn't permitted and the
     * connection hasn't started up, then the connect message is on deck.
     *
     * Besides initial session negotiation, we can also arrive here in the case where we have just
     * re-established a transport that hung up midway through session negotation.
     */
    const operation = this.txQueue[0]?.payload.operation;
    if (!operation || operation !== 'connect' && operation !== 'close')
    {
      let _resolve, _reject;

      debugging('connection') && console.debug(this.debugLabel, `top operation is ${operation}; queue service will wait for session|end`);
      ps.push(new Promise((resolve, reject) => {
        _resolve = resolve;
        _reject = () => reject(new DCPError('connection closed during session negotiation', this.closeErrorCode));
        this.on('ready', _resolve);
        this.on('end',   _resolve); /* might be trying to shutdown before establish */
        this.on('close', _reject);
      }));

      Promise.all(ps).then(() => {
        this.off('ready', _resolve);
        this.off('end',   _resolve);
        this.off('close', _reject);
      }).catch((error) => error, 'dont care');
    }
  }

  debugging('connection') && console.debug(this.debugLabel, 'scheduled queue service');
  this.txQueue.pendingService = true;

  Promise.all(ps)
    .then(() => this.serviceTxQueue())
    .catch(error => {
      if (this.state.is('closed'))
        return;
      this.txQueue.pendingService = false;
      debugging() && console.debug(this.debugLabel, 'cannot service queue;', error);
      this.shutdown(error, true);
    });
}

/**
 * Service the connection's txQueue. The idea here is that we enter this function, potentially more than
 * once, maybe racing through more than one call at a time. The txQueue.onDeck object serves as a mutex;
 * if there is a message onDeck when we get here, we bail and request another queue service on a later
 * event loop tick. That later request will probably find an empty queue, but it's better to service
 * the queue too often than it is to miss a necessary queue-pump due to a race condition that I haven't
 * foreseen.
 *
 * We then pull a message off the txQueue FIFO. This FIFO is pre-seeded with a connect message for
 * initiators during construction. If this is successful, the session has been established, and our
 * configuration allows it, we then try to pull more messages off the FIFO and combine them all into a
 * single batch message. Then we sign the message and try to transmit it. Once the remote peer
 * acknowleges receipt of the message, will pull it off deck, making way for us to transmit another
 * message.
 *
 * Message re-transmission is not handled by queue service; there are only two cases where we need to
 * retransmit the onDeck message:
 * - maxRTT timeout
 * - transport hangup
 *
 * Both of the retransmit mechanisms can cause duplicate messages to arrive at the peer. The peer stores
 * its most-recently-received messageId and discards duplicate messages. The peer always acknowledges
 * receipt of messages, even ones which have been discarded. Duplicate messages trigger duplicate acks.
 *
 * We also don't enter this function if there isn't a nonce available. There's two reasons for that:
 * 1) can't send DcpMessages without a nonce
 * 2) this implies that our last DcpMessage was not ack'd yet.
 *
 * @note treat as infallible
 * @note never call directly; always requestTxQueueService.
 */
Connection.prototype.serviceTxQueue = async function Connection530$$serviceTxQueue()
{
  this.txQueue.pendingService = false;
  if (this.state.is('closed'))
  {
    debugging('connection') && console.debug(this.debugLabel, 'skipping queue service; connection is closed');
    return;
  }
  if (!this.txQueue.length)
  {
    debugging('connection') && console.debug(this.debugLabel, 'skipping queue service; nothing to do');
    return;
  }
  if (this.txQueue.onDeck) /* mutex -> cleared on Ack */
  {
    debugging('connection') && console.debug(this.debugLabel, 'delaying queue service; busy');
    if (!this.state.is('closed'))
      setTimeout(() => this.requestTxQueueService(), 10); /* ~spinlock, rare */
    return;
  }

  debugging('connection') && console.debug(this.debugLabel, 'servicing queue');

  /* Place a message and its associated state on deck. This state will not be shared with or used by
   * another message, except when a batch message containing this message replaces it on deck. Once
   * something becomes onDeck, below the combineToBatch() call, it is as good as sent.
   */
  const topMessage = this.txQueue.shift();
  const onDeck = this.txQueue.onDeck = { message: topMessage, ackWait: {}, tries: 0 };

  /* This test must happen on the same tick as function entry, for connect response to be able to
   * temporarily disable batching. During combineToBatch(), the onDeck message might become a Batch.
   *
   * Note - onDeck.entry is undefined during batching; this can affect code elsewhere.
   */
  if (this.options.allowBatch && this.state.in(['established', 'closing']))
  {
    await this.combineToBatch();
    assert(this.txQueue.onDeck === onDeck);
  }

  onDeck.entry = this.dcpMessageRegistry.locate(onDeck.message);
  onDeck.ackToken = this.makeAckToken();
  this.emit('beforeSend', onDeck.message);

  /* The ackWait promise resolves when the Ack matching this message is received. The actual
   * resolution of the promise is done in the handleAck code, but all of the behaviours dependent on
   * that promise are handled in the parts of the code where they matter -- for example, here we care
   * about message transmission, so we take care of cleaning up the RTT timeout, clearing the deck, and
   * emitting the sent event. The queue-service-wait code waits for this promise to be resolved along
   * with all of the other preconditions for queue-service, etc.
   */
  onDeck.ackWait.promise = new Promise((resolve, reject) => {
    onDeck.ackWait.resolve = resolve;
    onDeck.ackWait.reject = reject;
  });
  onDeck.ackWait.promise.then(ack => {
    assert(onDeck === this.txQueue.onDeck);
    if (onDeck.message.type === 'batch')
      for (let innerMessage of onDeck.entry.batch)
        this.emit('sent', innerMessage);
    this.emit('sent', onDeck.entry.message);
    clearTimeout(onDeck.rttTimerHnd);
    this.txQueue.onDeck = false;
  }).catch(error => { this.shutdown(error, true); }); /* sending Ack should be infallible unless we're closed */

  try
  {
    onDeck.sealedMessage = await onDeck.message.sign(onDeck.ackToken);
  }
  catch(error)
  {
    debugging() && console.debug(this.debugLabel, 'could not sign message;', error);
    this.shutdown(error, true);
    return;
  }

  if (this.state.is('closed')) /* finalized while we were batching or signing */
    return;

  try
  {
    /* In the case where there is no transport currently active, we do everything but actually send the
     * message out the door. When the transport connects, it will wake up and transmit the onDeck
     * message, the same as if we had had an RTT timeout or a transport hangup.
     */
    if (this.transport)
      this.transmitOnDeckMessage();
  }
  catch(error)
  {
    debugging() && console.debug(this.debugLabel, 'could not transmit message;', error);
    this.shutdown(error, true);
  }
}

/**
 * Handle a message originating from the message event of the underlying transport.
 * Treat as infallible. Friend API for use by Target.
 *
 * Maintenance note: must not await between invocation and Ack transmission; otherwise the nonce can get
 * out of order when receiving traffic very quickly.
 *
 * @param {string} sealedMessage
 * @param {object} [signedMessage] the result of JSON.parse(sealedMessage); only needed here if we
 *                                 are trying to avoid double-parsing.
 */
Connection.prototype.receive = function Connection530$$receive(sealedMessage, signedMessage)
{
  if (this.state.is('closed'))
    return;

  debugging('wire') && wireLog(this.debugLabel, sealedMessage, 'rx');
  try
  {
    /* Validate the basic signed message envelope before passing the body on to dispatcher. The first
     * peerAddress we see for a given connection is taken as authoritative, unless it was already
     * supplied by the constructor.
     */
    if (!signedMessage)
      signedMessage = JSON.parse(sealedMessage);
    /* Discover the peer's address by examining the envelope of the first signed message we receive */
    if (!this.peerAddress)
    {
      this.peerAddress = new wallet.Address(signedMessage.owner);
      debugging('connection') && console.debug(this.debugLabel, 'peer is', this.peerAddress);
    }
    if (!this.peerAddress.eq(signedMessage.owner))
      throw new DCPError(`incorrect peer address ${signedMessage.owner}`, 'EINVAL');

    if (!signedMessage.signature)
      throw new DCPError('message is missing signature', 'EINVAL');
    if (!signedMessage.body)
      throw new DCPError('message is missing body', 'EINVAL');

    let correctSignature = false;
    try
    {
      correctSignature = this.peerAddress.verifySignature(signedMessage.body, signedMessage.signature);
    }
    catch (e) { /* Unable to verify signature will throw EINVAL below */ }
    if (!correctSignature)
      throw new DCPError('message has invalid signature', 'EINVAL');

    if (signedMessage.body.type === 'ack')
    {
      this.handleAck(signedMessage.body);
      return;
    }

    /* It's possible to receive the same DcpMessage twice, if the ack was lost in transit somehow. We
     * never act on a message twice, but we will acknowledge its receipt as many times as necessary.
     * When that happens, we don't generate a new nonce; we just retransmit the last Ack and hope the
     * peer is able to resynchronize.
     *
     * There's also an interesting race here, but it doesn't need fixing. If the peer transmits a given
     * message twice *so quickly* that the second instance arrives while the Ack for the first message
     * is still being signed, we will detect the dupe, but will respond with the Ack for the previous
     * message. That's okay, because as soon as the Ack finishes getting signed, we'll respond with the
     * right Ack. Making this any more robust is not necessary, because if that happens, there is a bug
     * in the peer anyhow. RTT should be measured in seconds.
     */
    if (this.lastRxMessageId && this.lastRxMessageId === signedMessage.body.id)
    {
      debugging('wire') && console.debug(this.debugLabel, 'DUP! msg', signedMessage.body.id);
      if (this.lastTxAckSealed !== null) /* null => fast dup of connect request */
        this.transmit(this.lastTxAckSealed);
      return;
    }
    this.lastRxMessageId = signedMessage.body.id;

    /* Acknowledge receipt of all DcpMessages ASAP. Acks are not enqueued with DcpMessages, but rather,
     * delivered immediately.
     */
    this.sendAck(signedMessage.body, this.makeNonce());

    if (this.dcpsid && this.dcpsid !== signedMessage.body.dcpsid)
      throw new DCPError('invalid dcpsid', 'EDCPSID');
    if (this.dcpsid)
      this.handleSignedMessage_haveSession(signedMessage.body);
    else
      this.handleSignedMessage_noSession(signedMessage.body);
  }
  catch(error)
  {
    debugging() && console.error(this.debugLabel, `error ${error.code || error.message} processing message; shutting down connection`, this);
    debugging('connection') && console.error(this.debugLabel, 'message body was:', signedMessage.body);
    if (debugging('connection') || error.code === 'EASSERT' || error.code === 'DCP_SYNCHRONIZER_ESYNC')
      console.error(this.debugLabel, 'error was:', error);
    this.shutdown(error);
  }
}

/**
 * Acknowledge receipt of a given DcpMessage. This tells the peer that they don't ever need to
 * retransmit the DcpMessage, provided they receive the Ack.
 *
 * The dcpsid is included in the Ack so that the lastAck can trigger the correct behaviour in the
 * remote during reconnect in version 5.2. This causes payload bloat and bad separation of concerns
 * that probably increases the bug surface. What should really happen is that the reconnect should
 * not be dependent on Ack messages for resynchronization, but rather, some kind of reconnect
 * message. See bug DCP-3957.
 *
 * Note that the actual network transmission happens asynchronously and, at the earliest, on the next
 * tick of the event loop.
 *
 * @param {string} messageBody  The signed message body which is the network form of the DcpMessage we
 *                              are acknowledging receipt of
 * @param {string} nextNonce    The next nonce we expect to receive from the peer
 */
Connection.prototype.sendAck = function Connection530$$sendAck(messageBody, nextNonce)
{
  const ack = new this.Ack(messageBody, nextNonce);

  this.ackSignPromise = this.identity.makeSignedMessage(ack.toNetwork())
    .catch(error => this.shutdown(error, true))
    .then(sealedAckTransportMessage => {
      if (this.state.is('closed'))
        return;
      this.emit('ack', ack);
      this.transmit(sealedAckTransportMessage);
      this.lastTxNonce = ack.nonce;
      this.lastTxAckSealed = sealedAckTransportMessage;
      this.lastTxAck = ack; /* eliminate when fixing DCP-3957 */
    })
    .finally(() => { this.ackSignPromise = false });
}

/**
 * Handle a signed message before we have established a session.  This allows us to skip some validation,
 * eg on nonces, but only Acks and Messages with operation=connect are allowed. Additionally, we don't
 * emit any events about DcpMessages while there is no session, so API consumers don't have to consider
 * the before-established-session case in their security model.
 *
 * @note Treat as infallible.
 *
 * @param signedMessage {object}       An ethereum signed message object; must contain body, owner,
 *                                     and signature properties. This function will validate that
 *                                     the signature belongs to the owner.
 */
Connection.prototype.handleSignedMessage_noSession = function handleSignedMessage_noSession(signedMessageBody)
{
  try
  {
    if (signedMessageBody.type === 'notification')
      throw new DCPError(`received notification before session fully established`, 'EINVAL');
    else if (signedMessageBody.type === 'request')
    {
      const operation = signedMessageBody.payload.operation;
      if (operation !== 'connect' && operation !== 'close')
        throw new DCPError(`received request for '${operation}' before session fully established`, 'EINVAL');
    }

    switch (signedMessageBody.type)
    {
      case 'request':
        if (!this.state.is('closing'))
          this.handleRequest(this.Request.from(signedMessageBody));
        break;
      case 'response':
        if (!this.clockInfo)
          this.clockInfo = { halfRTT: 0 }; /* populated in handleResponse, RTT TBD */
        this.handleResponse(this.Response.from(signedMessageBody));
        break;
      case 'batch':
        throw new DCPError('batch messages are prohibited before session establishment', 'EINVAL');
      default:
        throw new DCPError(`invalid message type '${signedMessageBody.type}'`, 'EINVAL');
    }
  }
  catch(error)
  {
    if (!error.code)
      error.code = 'DCPC-1025';
    debugging() && console.error(this.debugLabel, `Error processing pre-session ${signedMessageBody.type} message:`, error);
    this.shutdown(error);
  }
}

/**
 * In addition to the signed message validation in Connection530$$receive, DcpMessages which are part of a
 * session must have
 * - a dcpsid
 * - the nonce went sent with our last Ack message
 */
Connection.prototype.handleSignedMessage_haveSession = function handleSignedMessage_haveSession(signedMessageBody)
{
  const that = this;

  try
  {
    if (signedMessageBody.dcpsid !== this.dcpsid)
      throw new DCPError('message has incorrect dcpsid', 'EINVAL');
    if (!signedMessageBody.nonce || signedMessageBody.nonce !== this.lastTxNonce)
      throw new DCPError(`nonce mismatch (got ${signedMessageBody.nonce} !== want ${this.lastTxNonce}) in ${this.state} state`, 'DCPC-1026');
    else
      this.lastTxNonce = false; /* peer has consumed this nonce */

    handleMessageBody(signedMessageBody);
    function handleMessageBody(messageBody, inner)
    {
      switch (messageBody.type)
      {
        case 'request':
        {
          const request = that.Request.from(messageBody);
          if (!inner && that.state.is('closing'))
            debugging() && console.debug(that.debugLabel, `connection is closing; dropped ${request.payload.operation} request`, request);
          else
            that.handleRequest(request);
          break;
        }
        case 'response':
          that.handleResponse(that.Response.from(messageBody));
          break;
        case 'batch':
          if (inner)
            throw new DCPError('batch messages may not contain batch messages', 'EINVAL'); /* protect our stack */
          debugging('batch') && console.debug(that.debugLabel, 'handling batch of', messageBody.payload?.length, 'messages');
          for (let innerMessageBody of messageBody.payload)
            handleMessageBody(innerMessageBody, true);
          break;
        case 'notification':
        {
          const notification = that.Notification.from(messageBody);
          if (!inner && that.state.is('closing'))
            debugging() && console.debug(that.debugLabel, `connection is closing; dropped notification for ${notification.payload.operation}`, notification);
          else
            that.handleNotification(notification);
          break;
        }
        default:
          throw new DCPError(`invalid message type '${messageBody.type}'`, 'EINVAL');
      }
    }
  }
  catch(error)
  {
    if (!error.code)
      error.code = 'DCPC-1024';
    debugging() && console.error(this.debugLabel, `Error processing ${signedMessageBody.type} message:`, error);
    this.shutdown(error);
  }
}

/**
 * Handle a request from the peer. Invoked for arbitrary requests once the session has been established,
 * or requests with safe (connect, keepalive, close) operations before the session has been established.
 * The safe operations are handled internally, other operations are emitted with Connection<request>
 * events, with the expectation that the application layer will handle them.
 *
 * @param {object} request    instance of Request; object representing the request the peer sent us as
 *                            created by the Request.from().
 */
Connection.prototype.handleRequest = function Connection530$$handleRequest(request)
{
  assert(request instanceof this.Request);

  switch (request.payload.operation)
  {
    case 'connect':
      if (this.dcpsid || this.role.is('initiator'))
      {
        debugging() && console.debug(this.debugLabel, `unexpected request operation '${request.payload.operation}' - closing`);
        this.shutdown(new DCPError(`unexpected request operation '${request.payload.operation}'`, 'DCPC-1321'));
        return;
      }
      this.negotiateSession(request);
      break;
    case 'keepalive':
      request.respond({ success: true }).catch(error => this.shutdown(error));
      break;
    case 'close':
    {
      /* Peer is closing. Reject unsent request dialogue promises now to avoid waiting for closeTimeout. */
      const closeError = new DCPError('connection closed', 'ECONNRESET'); /* ECONNRESET => peer asked for close */
      this.dcpMessageRegistry.reject(closeError, 'request');

      /* Send a close response so the peer can stop waiting for closeTimeout. */
      const closeResponse = new this.Response(request, 'goodbye').unref();
      this.on('send', (message) => {
        if (this.state.is('closed'))
          return;
        if (message === closeResponse || message instanceof this.Batch && this.dcpMessageRegistry.locate(message).batch.includes(closeResponse))
          this.dcpMessageRegistry.locate(closeResponse).promiseHnd.resolve(); /* no need to wait for Ack */
      });
      closeResponse.send().catch(error => 'dont care');

      this.shutdown(closeError);
      break;
    }
    default:
    {
      try
      {
        if (!this.state.is('established'))
          throw new DCPError(`ignoring request while ${this.state}`, 'DCPC-1322'); /* reachable?? */

        const error = request.validityError();
        if (error === false)
          this.emit('request', request); /* this is where incoming requests meet the application layer */
        else
        {
          assert(error instanceof Error);
          debugging() && console.debug(this.debugLabel, `Request has invalid TTL (${error.code}); ignoring request. Validity=`, request.payload.validity);
          this.emit('error', error);
          request.respond(error).catch(error2 => {
            debugging() && console.error(this.debugLabel, 'Error responding to message with invalid TTL:', error2);
            this.shutdown(error2);
          });
        }
      }
      catch(error)
      {
        debugging() && console.error(this.debugLabel, 'request handler error: ', error);
        this.emit('error', error);
      }
      break;
    }
  }
}

/**
 * Handle a notification from the peer. Invoked for arbitrary notifications once the session has been
 * established.
 *
 * @param {object} notification   instance of Notification; object representing the request the peer sent
 *                                us as created by the Notification.from().
 */
Connection.prototype.handleNotification = function Connection530$$handleNotification(notification)
{
  assert(notification instanceof this.Notification)

  try
  {
    if (!this.state.is('established'))
      throw new DCPError(`ignoring notification while ${this.state}`, 'DCPC-1322'); /* reachable?? */

    const error = notification.validityError();
    if (error === false)
      this.emit('notification', notification); /* this is where incoming notifications meet the application layer */
    else
    {
      assert(error instanceof Error);
      debugging() && console.debug(this.debugLabel, `Notification has invalid TTL (${error.code}); ignoring request. Validity=`, notification.payload.validity);
      this.emit('error', error);
    }
  }
  catch(error)
  {
    debugging() && console.error(this.debugLabel, 'Notification handler error: ', error);
    this.emit('error', error);
  }
}

/**
 * Create an ack token
 * @returns {string}
 */
Connection.prototype.makeAckToken = function Connection530$$makeAckToken()
{
  return 'AT' + String(this.role)[0] + (++ackSeq) + '-' + randStr21();
}

/**
 * Create a nonce
 * @returns {string}
 */
Connection.prototype.makeNonce = function Connection530$$makeNonce()
{
  return 'NN' + String(this.role)[0] + (++nonceSeq) + '-' + randStr21();
}

/**
 * Handle an Ack received from peer. Acks tell us that the peer has received the DcpMessage associated
 * with the token in this Ack. That in turn lets us know we clear the on-deck object and resolve the
 * ackWait promise, which lets the queue service happen for the next message (assuming there's one
 * queued up)
 *
 * @param {object} ack    signed message body which is the network form of an Ack
 */
Connection.prototype.handleAck = function Connection530$$handleAck(ack)
{
  const onDeck = this.txQueue.onDeck;
  this.lastRxNonce = ack.nonce; /* DcpMessage class will use this nonce to transmit our next message */
  this.requestTxQueueService(); /* nonce available => we can service the queue */

  if (ack.token !== onDeck.ackToken)
  {
    if (ack.token === this.lastRxAckToken)
    {
      debugging('wire') && console.debug(this.debugLabel, 'DUP! ack', ack.token);
      if (this.txQueue.onDeck)
        this.transmitOnDeckMessage();
      debugging() && console.debug(this.debugLabel, `Retransmitted on-deck message due to duplicate ack ${ack.token}`);
    }
    else
    {
      debugging() && console.debug(this.debugLabel, `received unsolicited ack with ackToken ${ack.token} and dcpsid ${ack.dcpsid}; expected ${onDeck.ackToken} or ${this.lastRxAckToken}`);
      this.shutdown(new DCPError('unsolicted ack', 'EINVAL'));
    }
    return;
  }
  this.lastRxAckToken = ack.token;
  this.txQueue.onDeck.ackWait.resolve(ack); /* finish the send process, allowing more messages to go out */

  /* Response promises are resolved as soon as their receipt has been acknowleged, but Request promises
   * are delayed until the Reponse has been processed.
   */
  const { message, promiseHnd } = onDeck.entry;
  switch (message.type)
  {
    default:
      assert(!'reached');
    /* fall-through */
    case 'request':
    case 'notification':
      break;
    case 'batch':
      for (let innerMessage of onDeck.entry.batch)
      {
        const innerEntry = this.dcpMessageRegistry.locate(innerMessage);
        if (innerEntry?.message.type === 'response')
        {
          this.dcpMessageRegistry.remove(innerEntry.message);
          innerEntry.promiseHnd.resolve();
        }
      }
      this.dcpMessageRegistry.remove(message);
      break;
    case 'response':
      this.dcpMessageRegistry.remove(message);
      promiseHnd.resolve();
      break;
  }
}

/**
 * Handle a response message from the peer. If the response does not match a request we have sent and is
 * still in our registry, we assume the remote is an attacker and we close the connection. In the case
 * where the recent response gets retransmitted due to the session disconnecting, earlier code should
 * have detected it as a duplicate message and not invoked this function.
 *
 * If the response checks out, we remove it from our registry and use it to resolve the send-promise for
 * the associated request.
 *
 * @param {Object} Response    the response sent by the peer
 * @emits Connection<recvResponse>
 */
Connection.prototype.handleResponse = function Connection530$$handleResponse(response)
{
  if (!this.hasNtp)
  {
    let responseTime = response.time;
    if (!response.time)
    {
      const peerVersion = this.peerVersion || response.payload.operation === 'connection' && response.payload.version;
      if (semver.satisfies(peerVersion, '<5.3.0'))
        responseTime = Date.now(); /* bug work around; slated for removal mid-2024 /wg */
    }
    if (!responseTime)
      this.shutdown(new Error('invalid response time', 'EINVAL'));

    this.clockInfo.startNow = performance.now() / 1000;
    this.clockInfo.startTime = response.time;
  }

  const request = this.dcpMessageRegistry.getRequest(response);
  if (!request)
  {
    this.shutdown(new DCPError('received response for unknown message ' + response.id, 'EINVAL'));
    return;
  }

  this.emit('recvResponse', response, request);
  const { promiseHnd } = this.dcpMessageRegistry.locate(request);
  this.dcpMessageRegistry.remove(request);
  promiseHnd.resolve(response);
}

/**
 * Method is invoked when the transport disconnects. Transport instance is responsible for its own
 * finalization; Connection instance is responsible for finding a new transport, resuming the
 * connection, and retransmitting any in-flight message/ack.
 *
 * @param {object} transport    the transport instance that triggered this handler. In some cases, it
 *                              is possible that this event is not serviced until after the connection
 *                              has already acquired a new transport instance, e.g. in a Target where
 *                              the initiator switched IP address.
 */
Connection.prototype.hangupHandler = function Connection530$$hangupHandler(transport)
{
  if (transport !== this.transport || this.state.is('closed')) /* event no longer relevant */
    return;

  debugging('connection') && console.debug(this.debugLabel, 'hangupHandler invoked in state', String(this.state));
  this.transport = false;
  this.emit('disconnect', this.targetDescriptor.location); /* UI hint: "internet unavailable" */

  /* Gate; either transition established|connecting -> disconnected or bail */
  switch(String(this.state))
  {
    case 'initial':
    default:
      assert(false);
      break;
    case 'disconnected':
    case 'closing':
    case 'closed':
      return; /* transports may fire this handler more than once */
    case 'connecting': /* network interrupt during re-connection attempt, keep reconnecting until connectTimeout */
      if (this.connectingPromiseHnd)
        return; /* implies we are already in connect(); this situation handled in enlistTransport */
      /* fallthrough */
    case 'established':
      debugging('connection') && console.debug(this.debugLabel, `Transport disconnected from ${this.targetDescriptor.location} while ${this.state}`);
      this.state.set(['established', 'connecting'], 'disconnected');
      break;
  }

  if (this.role.is('target'))
  {
    /* targets generally can't reconnect due to NAT and more - instead we linger */
    if (this.dcpsid)
      debugging('connection') && console.debug(this.debugLabel, `Waiting for initiator to reconnect for ${this.dcpsid}`);
    else
    {
      debugging('connection') && console.debug(this.debugLabel, 'target connection has no dcpsid; cannot linger for reconnect');
      this.close(); /* can't linger when no dcpsid */
    }
    return;
  }

  if (this.options.onDemand && !this.hasPendingTx())
  {
    debugging('connection') && console.debug(this.debugLabel, `Not reconnecting ${this.dcpsid} until next message`);
    return;
  }

  /* At this point, we initiate a (re)connect attempt because either
   * - we have something to send, or
   * - we are not an on-demand connection
   */
  debugging('connection') && console.debug(this.debugLabel, 'hangupHandler invoking connect');
  this.connect().catch((error) => {
    if (error.code !== 'ETIMEDOUT' && error.code !== 'EPIPE')
      debugging('connection') && console.debug(this.debugLabel, 'unexpected error handling disconnect:', error);
  });
}

/**
 * Perform basic sanity checking on connection options.
 * @throws TypeError if insane options are specified
 */
function optionsSanityCheck(options)
{
  function notNegativeNumber(prop)
  {
    var value, path;

    for (value = options, path = prop.split('.');
         path.length;
         value = value[path.shift()])
      ; /* walk the dot path to the end */
    if (!(value > 0))
      throw new TypeError(`option ${prop} has invalid value ${options[prop]}`);
  }

  notNegativeNumber('identityUnlockTimeout');
  notNegativeNumber('connectTimeout');
  notNegativeNumber('reconnectTimeout');
  notNegativeNumber('lingerTimeout');
  notNegativeNumber('closeTimeout');
  notNegativeNumber('ttl.min');
  notNegativeNumber('ttl.max');
  notNegativeNumber('ttl.default');
}
